<template>
  <div class="funny-transform-button">
    <button
      class="btn btn-funny"
      :class="{ 'btn-loading': transforming, 'btn-disabled': disabled }"
      @click="handleTransformClick"
      :disabled="disabled || transforming"
      :title="creditTooltip"
    >
      <span class="btn-content">
        <span class="btn-icon">{{ transforming ? '⏳' : '😂' }}</span>
        <span class="btn-text">
          {{ transforming ? $t('editor.makingItFunny') : $t('editor.makeItFunny') }}
          <span v-if="intensity" class="intensity-badge">{{ intensity }}</span>
        </span>
      </span>
    </button>

    <!-- Job progress modal -->
    <JobProgress
      v-if="jobId"
      :jobId="jobId"
      jobType="style"
      :title="$t('editor.transformingResumeStyle')"
      :autoPoll="true"
      :pollInterval="2000"
      @completed="handleJobCompleted"
      @failed="handleJobFailed"
      @closed="handleJobProgressClosed"
    />

    <!-- Transformation preview modal -->
    <div v-if="showPreview" class="preview-modal">
      <div class="preview-overlay" @click="closePreview"></div>
      <div class="preview-content">
        <div class="preview-header">
          <h3>{{ $t('editor.funnyResumePreview') }}</h3>
          <button class="close-btn" @click="closePreview">×</button>
        </div>
        <div class="preview-body">
          <div class="preview-options">
            <div class="option-group">
              <label>{{ $t('editor.humorIntensity') }}</label>
              <div class="intensity-options">
                <button
                  v-for="level in intensityLevels"
                  :key="level.value"
                  class="intensity-option"
                  :class="{ 'active': previewIntensity === level.value }"
                  @click="previewIntensity = level.value"
                >
                  {{ $t('editor.' + level.value) }}
                </button>
              </div>
            </div>
            <div class="option-group">
              <label>
                <input type="checkbox" v-model="previewPreserveKeyInfo" />
                {{ $t('editor.preserveKeyInfo') }}
              </label>
            </div>
            <div class="option-group">
              <label>
                <input type="checkbox" v-model="useAsyncProcessing" />
                {{ $t('editor.useAsyncProcessingLongResumes') }}
              </label>
            </div>
          </div>
          
          <div class="preview-comparison">
            <div class="comparison-column">
              <h4>{{ $t('editor.original') }}</h4>
              <div class="preview-text original-text">
                {{ originalContentPreview }}
              </div>
            </div>
            <div class="comparison-column">
              <h4>{{ $t('editor.transformed') }}</h4>
              <div class="preview-text transformed-text">
                {{ transformedContentPreview }}
              </div>
            </div>
          </div>
        </div>
        <div class="preview-footer">
          <button class="btn btn-secondary" @click="closePreview">{{ $t('editor.cancel') }}</button>
          <button class="btn btn-primary" @click="applyTransformation" :disabled="!transformedContent">
            {{ $t('editor.applyTransformation') }}
          </button>
          <button class="btn btn-outline" @click="regeneratePreview" :disabled="regenerating">
            {{ regenerating ? $t('editor.regenerating') : $t('editor.tryDifferent') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Error toast -->
    <div v-if="errorMessage" class="error-toast">
      <div class="toast-content">
        <span class="toast-icon">⚠️</span>
        <span class="toast-message">{{ errorMessage }}</span>
        <button class="toast-close" @click="errorMessage = ''">×</button>
      </div>
    </div>

    <!-- Insufficient Credits Modal -->
    <InsufficientCreditsModal
      :show="showInsufficientCreditsModal"
      :required="creditErrorInfo?.required || 3"
      :balance="creditErrorInfo?.balance || creditBalance"
      :service="creditErrorInfo?.service || $t('editor.humorousResumeTransformation')"
      @close="closeInsufficientCreditsModal"
      @invite-friends="handleInviteFriends"
    />

    <!-- Invitation Modal -->
    <InvitationModal
      :isVisible="showInvitationModal"
      @close="closeInvitationModal"
      @success="handleInvitationSuccess"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue3-toastify'
import { useCreditStore } from '../stores/credits'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'
import JobProgress from './JobProgress.vue'
import InsufficientCreditsModal from './InsufficientCreditsModal.vue'
import InvitationModal from './InvitationModal.vue'

export default {
  name: 'FunnyTransformButton',
  components: {
    JobProgress,
    InsufficientCreditsModal,
    InvitationModal
  },
  props: {
    sessionId: {
      type: String,
      required: true
    },
    markdown: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    intensity: {
      type: String,
      default: 'medium',
      validator: (value) => ['subtle', 'medium', 'overt'].includes(value)
    }
  },
  emits: [
    'transform-start',
    'transform-success',
    'transform-error',
    'transform-preview',
    'needs-auth'
  ],
  setup(props, { emit }) {
    const { t } = useI18n()
    const creditStore = useCreditStore()
    const authStore = useAuthStore()
    const transforming = ref(false)
    const showPreview = ref(false)
    const transformedContent = ref('')
    const originalContent = ref('')
    const previewIntensity = ref(props.intensity)
    const previewPreserveKeyInfo = ref(true)
    const useAsyncProcessing = ref(true) // Default to async for long transformations
    const errorMessage = ref('')
    const regenerating = ref(false)
    const jobId = ref(null)
    const showInsufficientCreditsModal = ref(false)
    const creditErrorInfo = ref(null)
    const showInvitationModal = ref(false)

    const intensityLevels = [
      { value: 'subtle', label: 'Subtle' },
      { value: 'medium', label: 'Medium' },
      { value: 'overt', label: 'Overt' }
    ]

    const originalContentPreview = computed(() => {
      if (!originalContent.value) return 'No content available'
      return originalContent.value.length > 500 
        ? originalContent.value.substring(0, 500) + '...' 
        : originalContent.value
    })

    const transformedContentPreview = computed(() => {
      if (!transformedContent.value) return 'Transformation not yet generated'
      return transformedContent.value.length > 500 
        ? transformedContent.value.substring(0, 500) + '...' 
        : transformedContent.value
    })

    // Credit check computed properties (for pre-check before API call, not button state)
    const creditBalance = computed(() => creditStore.currentBalance)
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const creditTooltip = computed(() => {
      // No tooltip for unauthenticated users
      if (!isAuthenticated.value) {
        return ''
      }
      // For authenticated users, show credit cost info
      return `Cost: 3 credits (You have ${creditBalance.value})`
    })

    const handleTransformClick = async () => {
      if (!props.markdown || props.markdown.trim().length < 50) {
        errorMessage.value = 'Resume content is too short. Please add more content first.'
        emit('transform-error', new Error('Content too short'))
        return
      }

      // Check authentication first (before any API call)
      if (!isAuthenticated.value) {
        // Emit event to show auth modal
        emit('needs-auth')
        return // No API call, show auth modal
      }

      // Check credits before API call (only for authenticated users)
      if (!creditStore.canUseService) {
        showInsufficientCreditsModal.value = true
        creditErrorInfo.value = {
          required: 3,
          balance: creditBalance.value,
          service: t('editor.humorousResumeTransformation')
        }
        return // No API call, show insufficient credits modal
      }

      // User is authenticated AND has sufficient credits
      // Proceed with transformation
      transforming.value = true
      originalContent.value = props.markdown
      errorMessage.value = ''
      
      emit('transform-start', {
        sessionId: props.sessionId,
        intensity: props.intensity
      })

      try {
        console.log(`[FunnyTransformButton] Starting transformation with intensity: ${props.intensity}`)
        console.log(`[FunnyTransformButton] Using async processing: ${useAsyncProcessing.value}`)
        
        // Debug: Log the options being sent
        const transformOptions = {
          intensity: props.intensity,
          preserveKeyInfo: true,
          // Don't set default language - let backend auto-detect
          formatOutput: true
        };
        console.log(`[FunnyTransformButton] Transform options:`, transformOptions);
        console.log(`[FunnyTransformButton] Language in options: ${transformOptions.language === undefined ? '(not specified - will auto-detect)' : transformOptions.language}`);
        
        if (useAsyncProcessing.value) {
          // Use async job processing
          const jobResponse = await api.transformToHumorousAsync(
            props.sessionId,
            props.markdown,
            transformOptions
          )

          if (jobResponse.success) {
            jobId.value = jobResponse.jobId
            console.log(`[FunnyTransformButton] Async job created: ${jobId.value}`)
            toast.info('Style transformation job created. Processing in background...')
            // JobProgress component will handle polling and show progress
          } else {
            throw new Error(jobResponse.error || 'Failed to create async job')
          }
        } else {
          // Use synchronous processing (for backward compatibility)
          const response = await api.transformStyle(
            props.sessionId,
            props.markdown,
            'humorous',
            transformOptions
          )

          if (response.success) {
            transformedContent.value = response.transformedMarkdown
            console.log(`[FunnyTransformButton] Synchronous transformation successful. Length: ${response.transformedLength} chars`)
            
            emit('transform-success', {
              transformedMarkdown: response.transformedMarkdown,
              originalLength: response.originalLength,
              transformedLength: response.transformedLength,
              changesApplied: response.changesApplied,
              intensity: response.intensity
            })

            // Show preview automatically
            showPreview.value = true
            emit('transform-preview', {
              transformedMarkdown: response.transformedMarkdown,
              intensity: response.intensity
            })

            toast.success('Resume transformed successfully! Preview available.')
            
            // Refresh credit balance after successful transformation
            try {
              await creditStore.refreshBalance()
            } catch (refreshError) {
              console.error('Failed to refresh credit balance:', refreshError)
            }
          } else {
            throw new Error(response.error || 'Transformation failed')
          }
        }
      } catch (error) {
        console.error('[FunnyTransformButton] Transformation error:', error)
        
        // Handle 402 insufficient credits error
        if (error.isCreditError || error.response?.status === 402) {
          const credits = error.credits || {
            required: 3,
            balance: creditBalance.value,
            insufficient: 3 - creditBalance.value
          }
          showInsufficientCreditsModal.value = true
          creditErrorInfo.value = {
            required: credits.required || 3,
            balance: credits.balance || creditBalance.value,
            service: t('editor.humorousResumeTransformation')
          }
          
          // Refresh credit balance
          try {
            await creditStore.refreshBalance()
          } catch (refreshError) {
            console.error('Failed to refresh credit balance:', refreshError)
          }
          
          errorMessage.value = error.message || 'Insufficient credits to use this service.'
        } else {
          errorMessage.value = `Failed to transform resume: ${error.message}`
          toast.error('Failed to transform resume. Please try again.')
        }
        
        emit('transform-error', error)
      } finally {
        transforming.value = false
      }
    }

    const closeInsufficientCreditsModal = () => {
      showInsufficientCreditsModal.value = false
      creditErrorInfo.value = null
    }

    const handleInviteFriends = () => {
      showInvitationModal.value = true
    }

    const closeInvitationModal = () => {
      showInvitationModal.value = false
    }

    const handleInvitationSuccess = async (result) => {
      console.log('Invitation sent successfully:', result)
      // Refresh credit balance after invitation sent
      // (User may earn credits when invitation is accepted)
      try {
        await creditStore.refreshBalance()
      } catch (error) {
        console.error('Failed to refresh credit balance:', error)
      }
      closeInvitationModal()
    }

    const handleJobCompleted = async (jobResult) => {
      console.log('[FunnyTransformButton] Job completed:', jobResult)
      
      try {
        // Get the full job result with transformed markdown
        const resultResponse = await api.getStyleTransformJobResult(jobId.value)
        
        if (resultResponse.success) {
          transformedContent.value = resultResponse.transformedMarkdown
          
          emit('transform-success', {
            transformedMarkdown: resultResponse.transformedMarkdown,
            originalLength: resultResponse.originalLength,
            transformedLength: resultResponse.transformedLength,
            formattingApplied: resultResponse.formattingApplied,
            validation: resultResponse.validation,
            humorDensity: resultResponse.humorDensity,
            intensity: resultResponse.intensity
          })

          // Show preview automatically
          showPreview.value = true
          emit('transform-preview', {
            transformedMarkdown: resultResponse.transformedMarkdown,
            intensity: resultResponse.intensity
          })

          toast.success('Style transformation completed! Preview available.')
          
          // Refresh credit balance after successful transformation
          try {
            await creditStore.refreshBalance()
          } catch (refreshError) {
            console.error('Failed to refresh credit balance:', refreshError)
          }
        } else {
          throw new Error('Failed to get job result')
        }
      } catch (error) {
        console.error('[FunnyTransformButton] Failed to get job result:', error)
        errorMessage.value = `Transformation completed but failed to get result: ${error.message}`
        toast.error('Transformation completed but failed to get result.')
      } finally {
        jobId.value = null
      }
    }

    const handleJobFailed = (error) => {
      console.error('[FunnyTransformButton] Job failed:', error)
      errorMessage.value = `Async transformation failed: ${error}`
      emit('transform-error', new Error(`Async transformation failed: ${error}`))
      toast.error('Async transformation failed. Please try again.')
      jobId.value = null
    }

    const handleJobProgressClosed = () => {
      console.log('[FunnyTransformButton] Job progress modal closed')
      jobId.value = null
    }

    const regeneratePreview = async () => {
      if (!originalContent.value) return

      regenerating.value = true
      errorMessage.value = ''

      try {
        const response = await api.transformStyle(
          props.sessionId,
          originalContent.value,
          'humorous',
          {
            intensity: previewIntensity.value,
            preserveKeyInfo: previewPreserveKeyInfo.value
            // Don't set default language - let backend auto-detect
          }
        )

        if (response.success) {
          transformedContent.value = response.transformedMarkdown
          toast.success('New transformation generated!')
        } else {
          throw new Error(response.error || 'Regeneration failed')
        }
      } catch (error) {
        console.error('[FunnyTransformButton] Regeneration error:', error)
        errorMessage.value = `Failed to regenerate: ${error.message}`
        toast.error('Failed to regenerate transformation.')
      } finally {
        regenerating.value = false
      }
    }

    const applyTransformation = () => {
      if (!transformedContent.value) {
        errorMessage.value = 'No transformation to apply'
        return
      }

      // Emit event for parent to handle the application
      emit('transform-success', {
        transformedMarkdown: transformedContent.value,
        apply: true
      })

      closePreview()
      toast.success('Transformation applied to resume!')
    }

    const closePreview = () => {
      showPreview.value = false
      transformedContent.value = ''
    }

    // Watch for changes in props intensity
    watch(() => props.intensity, (newIntensity) => {
      previewIntensity.value = newIntensity
    })

    return {
      transforming,
      showPreview,
      transformedContent,
      originalContent,
      previewIntensity,
      previewPreserveKeyInfo,
      useAsyncProcessing,
      errorMessage,
      regenerating,
      jobId,
      creditBalance,
      creditTooltip,
      showInsufficientCreditsModal,
      creditErrorInfo,
      closeInsufficientCreditsModal,
      showInvitationModal,
      handleInviteFriends,
      closeInvitationModal,
      handleInvitationSuccess,
      intensityLevels,
      originalContentPreview,
      transformedContentPreview,
      handleTransformClick,
      handleJobCompleted,
      handleJobFailed,
      handleJobProgressClosed,
      regeneratePreview,
      applyTransformation,
      closePreview
    }
  }
}
</script>

<style scoped>
.funny-transform-button {
  position: relative;
  display: inline-block;
}

.btn-funny {
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%);
  color: #2d3748;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 1rem;
  box-shadow: 0 4px 6px rgba(255, 107, 107, 0.2);
}

.btn-funny:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(255, 107, 107, 0.3);
}

.btn-funny:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-loading {
  opacity: 0.8;
  cursor: wait;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-icon {
  font-size: 1.2rem;
}

.intensity-badge {
  background: rgba(255, 255, 255, 0.3);
  padding: 0.1rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
  text-transform: capitalize;
}

/* Preview Modal */
.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.preview-content {
  position: relative;
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.preview-header h3 {
  margin: 0;
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
  padding: 0.5rem;
  line-height: 1;
}

.close-btn:hover {
  color: #2d3748;
}

.preview-body {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
}

.preview-options {
  background: #f7fafc;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.option-group {
  margin-bottom: 1rem;
}

.option-group:last-child {
  margin-bottom: 0;
}

.option-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.intensity-options {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.intensity-option {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.intensity-option:hover {
  border-color: #cbd5e0;
}

.intensity-option.active {
  border-color: #ff6b6b;
  background: #fff5f5;
  color: #c53030;
  font-weight: 600;
}

.preview-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  height: 400px;
}

@media (max-width: 768px) {
  .preview-comparison {
    grid-template-columns: 1fr;
    height: auto;
  }
}

.comparison-column h4 {
  margin: 0 0 0.75rem 0;
  color: #4a5568;
  font-size: 1rem;
}

.preview-text {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  height: calc(100% - 2rem);
  overflow: auto;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.original-text {
  color: #4a5568;
}

.transformed-text {
  color: #2d3748;
  background: #fffaf0;
  border-color: #fed7d7;
}

.preview-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

.btn-primary {
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%);
  color: #2d3748;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(255, 107, 107, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline {
  background: white;
  border: 2px solid #e2e8f0;
  color: #4a5568;
}

.btn-outline:hover:not(:disabled) {
  border-color: #cbd5e0;
}

/* Error Toast */
.error-toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1001;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-content {
  background: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 400px;
}

.toast-icon {
  font-size: 1.2rem;
}

.toast-message {
  flex: 1;
  color: #c53030;
  font-size: 0.9rem;
}

.toast-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #c53030;
  padding: 0.25rem;
  line-height: 1;
}

.toast-close:hover {
  color: #9b2c2c;
}
</style>
